/*!
 * Copyright (c) Microsoft Corporation and contributors. All rights reserved.
 * Licensed under the MIT License.
 */
import { LoaderHeader, } from "@fluidframework/container-definitions/internal";
import { PerformanceEvent, createChildMonitoringContext, mixinMonitoringContext, sessionStorageConfigProvider, } from "@fluidframework/telemetry-utils/internal";
import { v4 as uuid } from "uuid";
import { Container } from "./container.js";
import { DebugLogger } from "./debugLogger.js";
import { pkgVersion } from "./packageVersion.js";
import { getAttachedContainerStateFromSerializedContainer, tryParseCompatibleResolvedUrl, } from "./utils.js";
function ensureResolvedUrlDefined(resolved) {
    if (resolved === undefined) {
        throw new Error(`Object is not a IResolveUrl.`);
    }
}
/**
 * @internal
 */
export class RelativeLoader {
    constructor(container, loader) {
        this.container = container;
        this.loader = loader;
    }
    async resolve(request) {
        if (request.url.startsWith("/")) {
            ensureResolvedUrlDefined(this.container.resolvedUrl);
            const container = await this.container.clone({
                resolvedUrl: { ...this.container.resolvedUrl },
                // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                version: request.headers?.[LoaderHeader.version] ?? undefined,
                // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                loadMode: request.headers?.[LoaderHeader.loadMode],
            }, {
                // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                canReconnect: request.headers?.[LoaderHeader.reconnect],
                // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                clientDetailsOverride: request.headers?.[LoaderHeader.clientDetails],
            });
            return container;
        }
        if (this.loader === undefined) {
            throw new Error("Cannot resolve external containers");
        }
        return this.loader.resolve(request);
    }
}
/**
 * Manages Fluid resource loading
 * @legacy
 * @alpha
 */
export class Loader {
    constructor(loaderProps) {
        const { urlResolver, documentServiceFactory, codeLoader, options, scope, logger, detachedBlobStorage, configProvider, protocolHandlerBuilder, } = loaderProps;
        const telemetryProps = {
            loaderId: uuid(),
            loaderVersion: pkgVersion,
        };
        const subMc = mixinMonitoringContext(DebugLogger.mixinDebugLogger("fluid:telemetry", logger, {
            all: telemetryProps,
        }), sessionStorageConfigProvider.value, configProvider);
        this.services = {
            urlResolver,
            documentServiceFactory,
            codeLoader,
            options: options ?? {},
            scope: options?.provideScopeLoader === false ? { ...scope } : { ...scope, ILoader: this },
            detachedBlobStorage,
            protocolHandlerBuilder,
            subLogger: subMc.logger,
        };
        this.mc = createChildMonitoringContext({
            logger: this.services.subLogger,
            namespace: "Loader",
        });
    }
    async createDetachedContainer(codeDetails, createDetachedProps) {
        return Container.createDetached({
            ...createDetachedProps,
            ...this.services,
        }, codeDetails);
    }
    async rehydrateDetachedContainerFromSnapshot(snapshot, createDetachedProps) {
        return Container.rehydrateDetachedFromSnapshot({
            ...createDetachedProps,
            ...this.services,
        }, snapshot);
    }
    async resolve(request, pendingLocalState) {
        const eventName = pendingLocalState === undefined ? "Resolve" : "ResolveWithPendingState";
        return PerformanceEvent.timedExecAsync(this.mc.logger, { eventName }, async () => {
            return this.resolveCore(request, getAttachedContainerStateFromSerializedContainer(pendingLocalState));
        });
    }
    async resolveCore(request, pendingLocalState) {
        const resolvedAsFluid = await this.services.urlResolver.resolve(request);
        ensureResolvedUrlDefined(resolvedAsFluid);
        // Parse URL into data stores
        const parsed = tryParseCompatibleResolvedUrl(resolvedAsFluid.url);
        if (parsed === undefined) {
            throw new Error(`Invalid URL ${resolvedAsFluid.url}`);
        }
        if (pendingLocalState !== undefined) {
            const parsedPendingUrl = tryParseCompatibleResolvedUrl(pendingLocalState.url);
            if (parsedPendingUrl?.id !== parsed.id ||
                parsedPendingUrl?.path.replace(/\/$/, "") !== parsed.path.replace(/\/$/, "")) {
                const message = `URL ${resolvedAsFluid.url} does not match pending state URL ${pendingLocalState.url}`;
                throw new Error(message);
            }
        }
        request.headers ?? (request.headers = {});
        // If set in both query string and headers, use query string.  Also write the value from the query string into the header either way.
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        request.headers[LoaderHeader.version] =
            parsed.version ?? request.headers[LoaderHeader.version];
        return this.loadContainer(request, resolvedAsFluid, pendingLocalState);
    }
    async loadContainer(request, resolvedUrl, pendingLocalState) {
        return Container.load({
            resolvedUrl,
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
            version: request.headers?.[LoaderHeader.version] ?? undefined,
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
            loadMode: request.headers?.[LoaderHeader.loadMode],
            pendingLocalState,
        }, {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
            canReconnect: request.headers?.[LoaderHeader.reconnect],
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
            clientDetailsOverride: request.headers?.[LoaderHeader.clientDetails],
            ...this.services,
        });
    }
}
//# sourceMappingURL=loader.js.map